import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Loader2,
  GripVertical,
  User,
  Award,
  Briefcase
} from 'lucide-react';
import {
  getSchoolTeamMembers,
  deleteTeamMember,
  updateMemberOrder,
  SchoolTeamMember
} from '@/actions/school-team';
import { showToast } from '@/components/ui/Toast';
import TeamMemberModal from './TeamMemberModal';

const TeamManagement: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [members, setMembers] = useState<SchoolTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState<SchoolTeamMember | null>(null);
  const [deletingMemberId, setDeletingMemberId] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchMembers();
    }
  }, [id]);

  const fetchMembers = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await getSchoolTeamMembers(id);

      if (response.success || response.status === 'success') {
        setMembers(response.data?.teamMembers || []);
      } else {
        showToast('Failed to load team members', 'error');
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      showToast('Failed to load team members', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingMember(null);
    setShowModal(true);
  };

  const handleEdit = (member: SchoolTeamMember) => {
    setEditingMember(member);
    setShowModal(true);
  };

  const handleDelete = async (member: SchoolTeamMember) => {
    if (!id) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${member.member_name}? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setDeletingMemberId(member.member_id);
      const response = await deleteTeamMember(id, member.member_id);

      if (response.success || response.status === 'success') {
        showToast('Team member deleted successfully', 'success');
        fetchMembers();
      } else {
        showToast('Failed to delete team member', 'error');
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      showToast('Failed to delete team member', 'error');
    } finally {
      setDeletingMemberId(null);
    }
  };

  const handleModalClose = (success?: boolean) => {
    setShowModal(false);
    setEditingMember(null);
    if (success) {
      fetchMembers();
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newMembers = [...members];
    const draggedItem = newMembers[draggedIndex];
    newMembers.splice(draggedIndex, 1);
    newMembers.splice(index, 0, draggedItem);

    setMembers(newMembers);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (!id || draggedIndex === null) return;

    try {
      // Update order numbers
      const updates = members.map((member, index) => ({
        member_id: member.member_id,
        member_order: index + 1
      }));

      await updateMemberOrder(id, { members: updates });
      showToast('Team member order updated', 'success');
    } catch (error) {
      console.error('Error updating order:', error);
      showToast('Failed to update order', 'error');
      fetchMembers(); // Reload to reset order
    } finally {
      setDraggedIndex(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brandColor animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <Users className="w-6 h-6" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Team Members
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Manage school staff and team members
              </p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Member
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        {members.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No team members yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Get started by adding your first team member
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Member
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member, index) => (
              <div
                key={member.member_id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-200 ${
                  draggedIndex === index ? 'opacity-50' : ''
                } cursor-move`}
              >
                {/* Image Section */}
                <div className="relative h-48 bg-gradient-to-br from-brandColor to-purple-600">
                  <div className="absolute top-2 left-2 p-1 bg-white/20 backdrop-blur-sm rounded cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-4 h-4 text-white" />
                  </div>
                  {member.member_image && member.member_image !== 'member_thumb.png' ? (
                    <img
                      src={member.member_image}
                      alt={member.member_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <User className="w-20 h-20 text-white/50" />
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {member.member_name}
                  </h3>

                  {member.member_designation && (
                    <div className="flex items-center text-sm text-brandColor mb-2">
                      <Briefcase className="w-4 h-4 mr-1" />
                      <span>{member.member_designation}</span>
                    </div>
                  )}

                  {member.member_qualification && (
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <Award className="w-4 h-4 mr-1" />
                      <span>{member.member_qualification}</span>
                    </div>
                  )}

                  {member.member_description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-4">
                      {member.member_description}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleEdit(member)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(member)}
                      disabled={deletingMemberId === member.member_id}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deletingMemberId === member.member_id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-1" />
                      )}
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <TeamMemberModal
          slateId={id!}
          member={editingMember}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default TeamManagement;
